import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum } from "~/types/enums";
import { Alert, Card, Grid, Icon, InputCheckbox, InputToggle, Picture, Spinner, Text } from "~/components-shared";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import linearLogo from "~/assets/i/apps/linear_logo.svg";
import { handleError, helpers } from "~/lib/helpers";
import styled from "styled-components";
const Actions = styled.div`
  position: relative;
  z-index: 2;
`;
const StyledGrid = styled(Grid)`
  position: relative;
  z-index: 2;
  max-height: 300px;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`;
const authorities = [AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE, AuthoritiesEnum.SIGN_UP_INTEGRATE];
const LinearIntegrationCard = _ref => {
  let {
    ...rest
  } = _ref;
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const posthog = usePostHog();
  const integration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.LINEAR));
  const [linearTeams, setLinearTeams] = useState([]);
  const [loadingLinearTeams, setLoadingLinearTeams] = useState(false);
  const [linearTeamsError, setLinearTeamsError] = useState();
  const [isExpanded, setIsExpanded] = useState(false);
  const handleLoadLinearTeams = () => {
    setLoadingLinearTeams(true);
    setLinearTeamsError(undefined);
    api.fetchLinearTeams().then(data => setLinearTeams(data)).catch(e => setLinearTeamsError(handleError(e))).finally(() => setLoadingLinearTeams(false));
  };
  useEffect(() => {
    if (integration && userHasOneOf(authorities)) {
      handleLoadLinearTeams();
    }
  }, [!!integration]);
  const handleToggle = value => {
    if (value) {
      api.linearInstall().then(_ref2 => {
        let {
          path
        } = _ref2;
        const width = 560;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(path, "Install Linear", `width=${width},height=${height},left=${left},top=${top}`);
      });
      setIsExpanded(true);
      posthog.capture(`Linear Integration – Install`);
    } else if (integration?.id) {
      dispatch(integrationsActionCreators.deleteStarted(integration.id));
      posthog.capture(`Linear Integration – Install`);
    }
  };
  const handleExpand = () => {
    setIsExpanded(prev => !prev);
  };
  const handleUpdate = teamId => {
    if (integration) {
      let teams = integration.settings?.teams || [];
      if (teams.includes(teamId)) {
        teams = teams.filter(id => id !== teamId);
      } else {
        teams.push(teamId);
      }
      dispatch(integrationsActionCreators.updateStarted(integration.id, {
        type: integration.type,
        token: integration.token,
        settings: {
          ...integration.settings,
          teams
        }
      }));
      posthog.capture(`Linear Integration – Update`, {
        ...helpers.objectDiff({
          ...integration.settings
        }, {
          ...integration.settings,
          teams
        })
      });
    }
  };
  return /*#__PURE__*/React.createElement(Card, _extends({
    onClick: userHasOneOf(authorities) && integration ? handleExpand : undefined,
    elemBefore: /*#__PURE__*/React.createElement(Picture, {
      src: linearLogo,
      width: "30px",
      height: "30px",
      border: "squared",
      color: "transparent",
      s: {
        mr: 2
      }
    }),
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      display: "inline-block"
    }, "Linear"), integration && userHasOneOf(authorities) && /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      src: "chevron",
      rotate: isExpanded ? 0 : 180,
      s: {
        ml: 1,
        mb: 1
      }
    })),
    elemAfter: /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(InputToggle, {
      value: !!integration,
      display: "inline-block",
      onChange: (value, event) => {
        event.stopPropagation();
        handleToggle(value);
      },
      disabled: !userHasOneOf(authorities)
    })),
    collapsed: !integration || !isExpanded,
    s: {
      mb: 3
    }
  }, rest), linearTeamsError && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, linearTeamsError), loadingLinearTeams ? /*#__PURE__*/React.createElement(Spinner, {
    center: true
  }) : /*#__PURE__*/React.createElement(StyledGrid, {
    cols: 2,
    template: "1rem 3rem 1fr",
    s: {
      pl: 2
    },
    gap: 3,
    justify: "center",
    fullWidth: true
  }, linearTeams.length ? linearTeams.map(team => /*#__PURE__*/React.createElement(React.Fragment, {
    key: team.id
  }, /*#__PURE__*/React.createElement(InputCheckbox, {
    value: integration?.settings?.teams?.includes(team.id),
    onChange: () => handleUpdate(team.id),
    s: {
      mt: 1
    }
  }), /*#__PURE__*/React.createElement(Picture, {
    width: "3rem",
    height: "3rem",
    name: team.name.substring(0, 1).toUpperCase(),
    color: team.color
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Text, {
    display: "block",
    fontSize: 4,
    s: {
      pt: 1
    },
    weight: "semiBold"
  }, team.name, " (", team.key, ")"), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    weight: "semiBold",
    color: "textSecondary"
  }, team.description || "No description")))) : /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary"
  }, "No Teams")));
};
export default LinearIntegrationCard;